// src/useAsync.ts
import { useCallback, useEffect, useRef, useState } from "react";
var toApiError = (error) => {
  if (error && typeof error === "object" && "kind" in error && "message" in error) {
    return error;
  }
  return { kind: "unknown", message: String(error), cause: error };
};
var useAsync = (asyncFn, options) => {
  const { immediate = true, deps = [] } = options ?? {};
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const asyncFnRef = useRef(asyncFn);
  asyncFnRef.current = asyncFn;
  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await asyncFnRef.current(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(toApiError(err));
      return void 0;
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!immediate) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    asyncFnRef.current(...[]).then((result) => {
      if (!cancelled) {
        setData(result);
      }
    }).catch((err) => {
      if (!cancelled) {
        setError(toApiError(err));
      }
    }).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, deps);
  return { data, loading, error, execute };
};

export {
  useAsync
};
